import type { Component } from "vue";

import { isB2C, isXS2EventShop } from "@/brandconfig";
import { useUserStore } from "@/store/user";

import { getPath } from "@/components/event";
import Quotes from "@/pages/partners/Quotes.vue";
import { eventsWithSportType } from "@/constants/routes";

import type { NavigationGuard, RouteRecordRaw, RouteLocationNormalizedLoaded } from "vue-router";

const AboutPage = () => import("@/pages/AboutPage.vue");
const AllEventsPage = () => import("@/pages/AllEventsPage.vue");
const AllTournamentsPage = () => import("@/pages/AllTournamentsPage.vue");
const AllocationsPage = () => import("@/pages/AllocationsPage.vue");
const BookingsPage = () => import("@/pages/bookings/BookingsPage.vue");

const CheckoutPage = () => import("@/pages/CheckoutPage.vue");
const CheckoutPaymentCompletedPage = () => import("@/pages/CheckoutPaymentCompletedPage.vue");
const CheckoutPaymentCompletedLatePage = () => import("@/pages/CheckoutPaymentCompletedLatePage.vue");
const CheckoutPaymentPendingPage = () => import("@/pages/CheckoutPaymentPendingPage.vue");
const EventPage = () => import("@/pages/EventPage.vue");
const HomePage = () => import("@/pages/HomePage.vue");

const FAQPage = () => import("@/pages/FAQPage.vue");
const InvoicesPage = () => import("@/pages/receivables/InvoicesPage.vue");
const ListingsSalesContainer = () => import("@/pages/listings/ListingsSalesContainer.vue");
const PartnersPage = () => import("@/pages/partners/PartnersPage.vue");
const PrivacyPage = () => import("@/pages/PrivacyPage.vue");
const ProfilePage = () => import("@/pages/ProfilePage.vue");
const TermsPage = () => import("@/pages/TermsPage.vue");
const View404 = () => import("@/pages/ErrorPage.vue");
const WatchlistPage = () => import("@/pages/WatchlistPage.vue");

const PriorityThankYou = () => import("@/pages/PriorityThankYou.vue");
import Xs2TicketPage from "@/components/page/Xs2TicketPage.vue";
import AuthLayout from "@/components/page/AuthLayout.vue";
import SignInHeroLayout from "@/components/sign-in/SignInHeroLayout.vue";
import CheckoutBookingDetails from "@/components/checkout/b2c/CheckoutBookingDetails.vue";
import CheckoutOrderConfirmation from "@/components/checkout/b2c/CheckoutOrderConfirmation.vue";
import { useEvent } from "@xs2event/js-lib/api";
import { until } from "@vueuse/core";
import { camelToSnake } from "@xs2event/js-lib/util";
import { SportTypeOptionsV2 } from "@xs2event/js-lib";
import { lookupSportTypeName, useSportTypesStore } from "@/store/sportTypes.js";
import { getTeamBySlug } from "@/composables/teams";

const requireUnauthenticatedUser: NavigationGuard = async (_, _2, next) => {
  const userStore = useUserStore();

  if (isB2C) {
    return next("/");
  }

  try {
    await userStore.authCompleted;

    if (userStore.username) {
      return next("/");
    }
  } catch (err) {
    console.warn(err);
    //Authentication failed, continue
    return next();
  }
  next();
};

const requireUserOrWebshop: NavigationGuard = async (to, _, next) => {
  const userStore = useUserStore();

  try {
    await userStore.authCompleted;
    if (!userStore.username) {
      if (to.fullPath !== "/") {
        return next({ path: "/sign-in", query: { redirect: to.fullPath } });
      } else {
        return next("/sign-in");
      }
    }
    if (!isB2C && !userStore.isWebshop && userStore.needsPasswordReset) {
      return next({ path: "/change-password", query: { redirect: to.fullPath } });
    }
  } catch (err) {
    console.warn(err);
  }
  next();
};

const requireUser: NavigationGuard = async (to, _, next) => {
  const userStore = useUserStore();

  try {
    await userStore.authCompleted;
    if (!userStore.username || userStore.isWebshop) {
      if (to.fullPath !== "/") {
        return next({ path: "/sign-in", query: { redirect: to.fullPath } });
      } else {
        return next("/sign-in");
      }
    }
    if (to.path !== "/change-password" && userStore.needsPasswordReset) {
      return next({ path: "/change-password", query: { redirect: to.fullPath } });
    }
  } catch (err) {
    console.warn(err);
  }
  next();
};

type BreadCrumb = { label: string; to?: string | Record<string, unknown> };

type Route = RouteRecordRaw & {
  // Vue Router redirect routes support an optional `query` object.
  query?: Record<string, unknown>;
  meta?: {
    layout?: Component | false;
    path?: BreadCrumb[];
    title?: string;
    extraContent?: Component;
    dynamicListingBreadcrumbs?: boolean;
  };
};

const singleQueryValue = (value: unknown): string | undefined => {
  if (typeof value === "string")
    return value
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean)[0];
  if (Array.isArray(value) && typeof value[0] === "string") return value[0];
  return undefined;
};

const normalizeSportTypeKey = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]/g, "");

export const listingBreadCrumbs = async (to: RouteLocationNormalizedLoaded): Promise<BreadCrumb[]> => {
  const base: BreadCrumb[] = [{ label: "Home", to: "/" }];

  const querySportType = singleQueryValue(to.query?.sport_type);
  const paramSportType = typeof to.params?.sport_type === "string" ? to.params.sport_type : undefined;
  const selectedSportType = querySportType || paramSportType;

  if (!selectedSportType) {
    return [...base, { label: "Events" }];
  }

  const normalizedSportType = selectedSportType === "football" ? "soccer" : selectedSportType;
  const sportOption =
    SportTypeOptionsV2.find((opt) => opt.value === normalizedSportType) ||
    SportTypeOptionsV2.find((opt) => normalizeSportTypeKey(opt.value) === normalizeSportTypeKey(normalizedSportType));

  const sportLabel = sportOption?.label || lookupSportTypeName(normalizedSportType);
  const language = typeof to.params?.language === "string" ? to.params.language : undefined;
  const eventsPath = language ? `/${language}/events` : "/events";
  const tournamentsPath = language
    ? `/${language}/${selectedSportType}/tournaments`
    : `/${selectedSportType}/tournaments`;
  const sportCrumbShouldGoToTournaments = selectedSportType === "tennis" || selectedSportType === "football";

  const crumbs: BreadCrumb[] = [
    ...base,
    {
      label: sportLabel,
      to: sportCrumbShouldGoToTournaments
        ? tournamentsPath
        : {
            path: eventsPath,
            query: { sport_type: selectedSportType },
          },
    },
  ];

  const tournamentSlug = singleQueryValue(to.query?.tournament);
  if (tournamentSlug) {
    const sportTypesStore = useSportTypesStore();
    await sportTypesStore.ready;
    const tournament = sportTypesStore.getTournament(tournamentSlug);
    if (tournament?.official_name) {
      crumbs.push({ label: tournament.official_name });
    }
  }

  const teamSlug = singleQueryValue(to.query?.team);
  if (teamSlug) {
    const team = await getTeamBySlug(teamSlug);
    if (team?.officialName) {
      crumbs.push({ label: team.officialName });
    }
  }

  return crumbs;
};

export const routes: Route[] = [
  { path: "/priority/thankyou", component: PriorityThankYou, meta: { layout: false } },
  {
    name: "home",
    path: "/",
    component: HomePage,
    beforeEnter: requireUserOrWebshop,
    meta: {
      pageBackground: isXS2EventShop ? "bg-transparent" : "bg-surface",
      pageContainer: false,
    },
  },
  {
    path: "/:language?/checkout",
    component: CheckoutPage,
    name: "checkout",
    query: { paymentStatus: null },
    meta: { layout: false },
    props: true,
    beforeEnter: requireUserOrWebshop,
  },
  {
    path: "/:language?/checkout/booking-details", // Checkout step 1
    component: CheckoutBookingDetails,
    name: "checkout-booking-details",
    meta: { layout: false },
    props: true,
    beforeEnter: requireUserOrWebshop,
  },
  {
    path: "/:language?/checkout/order-confirmation", // Checkout step 2
    component: CheckoutOrderConfirmation,
    name: "checkout-order-confirmation",
    query: { paymentStatus: null },
    meta: { layout: false },
    props: true,
    beforeEnter: requireUserOrWebshop,
  },

  {
    path: "/:language?/checkout/payment-complete/:reservationId",
    name: "payment-complete",
    component: CheckoutPaymentCompletedPage,
    props: true,
    meta: {
      title: "Checkout completed",
    },
    beforeEnter: requireUserOrWebshop,
  },
  {
    path: "/:language?/checkout/payment-complete-late/:bookingOrderId",
    name: "payment-complete-late",
    component: CheckoutPaymentCompletedLatePage,
    props: true,
    meta: {
      title: "Payment completed",
    },
    beforeEnter: requireUserOrWebshop,
  },
  {
    path: "/:language?/paymentpending/:reservationId",
    component: CheckoutPaymentPendingPage,
    props: true,
    meta: {
      title: "Checkout payment pending",
    },
    beforeEnter: requireUserOrWebshop,
  },
  {
    path: "/:language?/events",
    component: AllEventsPage,
    beforeEnter: [requireUserOrWebshop],
    meta: {
      path: [{ label: "Home", to: "/" }, { label: "Events" }],
      title: "All events",
      dynamicListingBreadcrumbs: true,
    },
  },
  // {
  //   path: "/:language?/new-events",
  //   name: "new-events",
  //   component: AllEventsPage,
  //   beforeEnter: requireUserOrWebshop,
  //   meta: { path: [{ label: "Home", to: "/" }, { label: "New events" }], title: "New events" },
  //   props: { sort: "created:desc", limit: 25 },
  // },
  {
    path: "/:language?/all-events",
    component: AllEventsPage,
    beforeEnter: [requireUserOrWebshop],
    meta: {
      path: [{ label: "Home", to: "/" }, { label: "Events" }],
      title: "All events",
      dynamicListingBreadcrumbs: true,
    },
  },
  { path: "/:language?/football", redirect: eventsWithSportType("football") },
  { path: "/:language?/motorsport", redirect: eventsWithSportType("motorsport") },
  { path: "/:language?/motorsport/formula-1", redirect: eventsWithSportType("formula1") },
  {
    path: "/:sport_type/tournaments",
    name: "tournaments-page",
    component: AllTournamentsPage,
    beforeEnter: [requireUserOrWebshop],
    props: true,
    meta: {
      path: [{ label: "Home", to: "/" }, { label: "Tournaments" }],
      title: "Tournaments",
      dynamicListingBreadcrumbs: true,
    },
  },
  {
    path: "/:language/:sport_type/tournaments",
    name: "tournaments-page-with-language",
    component: AllTournamentsPage,
    beforeEnter: [requireUserOrWebshop],
    props: true,
    meta: {
      path: [{ label: "Home", to: "/" }, { label: "Tournaments" }],
      title: "Tournaments",
      dynamicListingBreadcrumbs: true,
    },
  },
  { path: "/:language?/user/profile", component: ProfilePage, beforeEnter: requireUser, meta: { title: "Profile" } },
  {
    path: "/:language?/user/bookings",
    name: "bookings",
    component: BookingsPage,
    props: true,
    meta: {
      title: "Bookings",
      path: [{ label: "Home", to: "/" }, { label: "Bookings" }],
    },
    beforeEnter: requireUser,
  },
  {
    path: "/:language?/user/watchlist",
    name: "watchlist",
    component: WatchlistPage,
    props: true,
    meta: {
      title: "Watchlist",
      path: [{ label: "Home", to: "/" }, { label: "Watchlist" }],
    },
    beforeEnter: requireUser,
  },
  {
    path: "/:language?/user/allocations",
    name: "allocations",
    component: AllocationsPage,
    props: true,
    meta: {
      title: "Allocations",
      path: [{ label: "Home", to: "/" }, { label: "Allocations" }],
    },
    beforeEnter: requireUser,
  },
  {
    path: "/:language?/user/invoices",
    name: "invoices",
    component: InvoicesPage,
    props: true,
    beforeEnter: requireUser,
    meta: {
      title: "Open invoices",
      path: [{ label: "Home", to: "/" }, { label: "Open invoices" }],
    },
  },
  {
    path: "/:language?/user/sales/listings",
    name: "listings",
    component: ListingsSalesContainer,
    props: true,
    meta: {
      title: "Relistings & Sales",
      path: [{ label: "Home", to: "/" }, { label: "Relistings & Sales" }],
    },
    beforeEnter: requireUser,
  },
  { path: "/:language?/about", component: AboutPage, meta: { layout: false }, beforeEnter: requireUserOrWebshop },
  { path: "/:language?/terms", component: TermsPage, meta: { layout: false }, beforeEnter: requireUserOrWebshop },
  { path: "/:language?/faq", component: FAQPage, beforeEnter: requireUserOrWebshop, meta: { title: "FAQ" } },
  { path: "/:language?/privacy-policy", component: PrivacyPage, beforeEnter: requireUserOrWebshop },
  {
    path: "/:language?/partners",
    component: PartnersPage,
    beforeEnter: requireUser,
    meta: { title: "Partnerships and cooperation", extraContent: Quotes },
  },

  { path: "/:language?/tennis", redirect: eventsWithSportType("tennis"), query: { sport_type: "tennis" } },
  { path: "/:language?/rugby", redirect: eventsWithSportType("rugby"), query: { sport_type: "rugby" } },
  {
    path: "/:language?/horseracing",
    redirect: eventsWithSportType("horseracing"),
    query: { sport_type: "horseracing" },
  },
  {
    path: "/:language?/combatsport",
    redirect: eventsWithSportType("combatsport"),
    query: { sport_type: "combatsport" },
  },
  { path: "/:language?/basketball", redirect: eventsWithSportType("basketball"), query: { sport_type: "basketball" } },
  { path: "/:language?/cricket", redirect: eventsWithSportType("cricket"), query: { sport_type: "cricket" } },

  {
    path: "/:language?/sign-in",
    component: () => import("@/pages/SignInPage.vue"),
    meta: { layout: SignInHeroLayout },
    beforeEnter: requireUnauthenticatedUser,
  },
  {
    path: "/:language?/sign-up",
    component: () => import("@/pages/SignUpPage.vue"),
    meta: { layout: SignInHeroLayout, wideCard: true, hero: "onboarding" },
    beforeEnter: requireUnauthenticatedUser,
  },
  {
    path: "/:language?/register",
    redirect: (to) => {
      const { language } = to.params;
      return language ? `/${language}/sign-up` : "/sign-up";
    },
  },
  {
    path: "/:language?/sign-up/activation",
    component: () => import("@/pages/ActivationPage.vue"),
    props: true,
    meta: { layout: SignInHeroLayout, wideCard: true, hero: "activation" },
    beforeEnter: requireUnauthenticatedUser,
  }, // to set password after sign-up
  {
    path: "/:language?/forgot-password",
    component: () => import("@/pages/PasswordResetPage.vue"),
    meta: { layout: SignInHeroLayout, wideCard: true, hero: "recovery" },
    beforeEnter: requireUnauthenticatedUser,
  }, // forgot password request page
  {
    path: "/:language?/reset-password/:username/:resettoken",
    component: () => import("@/pages/NewPasswordPage.vue"),
    meta: {
      layout: AuthLayout,
      title: "Reset your password",
      description: "Please enter a new password to login into the portal",
    },
    beforeEnter: requireUnauthenticatedUser,
  }, // choose new password after reset request
  {
    path: "/change-password",
    component: () => import("@/pages/ForceChangePasswordPage.vue"),
    beforeEnter: requireUser,
    meta: {
      layout: AuthLayout,
      title: "Change your password",
      description: "For security reasons you need to change your password before continuing.",
    },
  },
  { path: "/:language?/:notFound(.*)*", component: View404, props: { error: 404 } },
  {
    path: "/:language?/:eventId",
    name: "event-page",
    component: EventPage,
    props: true,
    meta: {
      skipMetaTagsHandler: true,
    },
    beforeEnter: [
      requireUserOrWebshop,
      async (to, _, next) => {
        const { data: event, isError } = useEvent({ eventId: to.params.eventId as string }, { retry: false });
        await until(() => event.value || isError.value).toBeTruthy();
        if (isError.value) {
          to.meta.error = 404;
          return next();
        }

        to.meta.path = getPath(camelToSnake(event.value));
        next();
      },
    ],
  },

  // B2C Ticket download
  {
    path: "/:language?/xs2ticket/download/:code",
    component: () => import("@/pages/DownloadTicketPage.vue"),
    beforeEnter: requireUserOrWebshop,
    meta: {
      layout: Xs2TicketPage,
    },
  },
  {
    path: "/:language?/xs2ticket",
    props: true,
    component: () => import("@/pages/RequestTicketPage.vue"),
    beforeEnter: requireUserOrWebshop,
    meta: {
      layout: Xs2TicketPage,
    },
  },
];
